import { Stack, Typography } from "@mui/material";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import DonutLargeOutlinedIcon from "@mui/icons-material/DonutLargeOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import type { CorporateListedInfo } from "../types";
import { TimeSeriesChart } from "./TimeSeriesChart";
import { DataTable } from "./DataTable";
import { EmptyAlert } from "./EmptyAlert";
import { Fields } from "./Fields";
import {
  formatBirthYear,
  formatDate,
  formatNumberOrDash,
  formatRatioAsPercent,
  formatYenOrDash,
} from "../utils/format";

// 決算期（YYYY-MM）の昇順比較。欠損は先頭（古い側）に置く。
const byFiscalPeriodAsc = (
  a: CorporateListedInfo["financials"][number],
  b: CorporateListedInfo["financials"][number]
): number =>
  (a.fiscalPeriodEndingYearMonth ?? "").localeCompare(
    b.fiscalPeriodEndingYearMonth ?? ""
  );
export const ListedInfoTab: React.FC<{
  listed: CorporateListedInfo | null;
}> = ({ listed }) => {
  if (!listed)
    return <EmptyAlert>有報情報なし（非上場または未取得）</EmptyAlert>;
  // 決算期別財務は fiscal 昇順で時系列グラフにする。
  const finAsc = [...(listed.financials ?? [])].sort(byFiscalPeriodAsc);
  const finLabels = finAsc.map((f) => f.fiscalPeriodEndingYearMonth ?? "—");
  return (
    <Stack spacing={3}>
      <Typography variant="caption" color="text.secondary">
        出典：有価証券報告書（EDINET）
      </Typography>
      <Fields
        items={[
          ["ティッカー", listed.tickerCode],
          ["市場", listed.marketSegment],
          ["JPX33業種コード", listed.industryCodeJpx33],
          [
            "従業員数",
            formatNumberOrDash(
              listed.employeeStatus?.employeeCount ?? null,
              "人"
            ),
          ],
          [
            "平均年齢",
            listed.employeeStatus?.averageAge != null
              ? `${listed.employeeStatus.averageAge}歳`
              : "—",
          ],
          [
            "平均勤続年数",
            listed.employeeStatus?.averageYearsOfService != null
              ? `${listed.employeeStatus.averageYearsOfService}年`
              : "—",
          ],
          [
            "平均年間給与",
            formatYenOrDash(listed.employeeStatus?.averageAnnualSalary ?? null),
          ],
        ]}
      />
      <DataTable
        icon={<DonutLargeOutlinedIcon />}
        title="セグメント情報"
        headers={["セグメント", "区分", "事業内容"]}
        rows={(listed.segments ?? []).map((s) => [
          s.name,
          s.kind === "reportable"
            ? "報告セグメント"
            : s.kind === "other"
            ? "その他"
            : s.kind ?? "—",
          s.description,
        ])}
      />
      <DataTable
        icon={<AccountTreeOutlinedIcon />}
        title="関係会社の状況"
        headers={[
          "会社名",
          "所在地",
          "議決権所有割合",
          "主要な事業内容",
          "業種",
        ]}
        rows={(listed.affiliates ?? []).map((a) => [
          a.name,
          a.location,
          // 関係会社の議決権所有割合は %値（100.0 = 100%）。
          formatNumberOrDash(a.votingRightsRatio, "%"),
          a.relationship,
          a.industry ?? "—",
        ])}
      />
      <DataTable
        icon={<ShowChartOutlinedIcon />}
        title="決算期別財務"
        headers={[
          "決算期",
          "売上高（円）",
          "営業利益（円）",
          "経常利益（円）",
          "純利益（円）",
          "EPS（円）",
          "ROE",
          "自己資本比率",
          "1株配当（円）",
        ]}
        rows={[...finAsc].reverse().map((f) => [
          f.fiscalPeriodEndingYearMonth,
          formatYenOrDash(f.sales),
          formatYenOrDash(f.operatingProfitLoss),
          formatYenOrDash(f.ordinaryIncome),
          formatYenOrDash(f.profitLossToParent),
          formatYenOrDash(f.earningsPerShare),
          // ROE・自己資本比率は小数の比率（0.1393 = 13.93%）。
          formatRatioAsPercent(f.rateOfReturnOnEquity),
          formatRatioAsPercent(f.equityToAssetRatio),
          formatYenOrDash(f.dividendPerShare),
        ])}
      />
      <TimeSeriesChart
        icon={<ShowChartOutlinedIcon />}
        title="業績推移（有価証券報告書）"
        labels={finLabels}
        series={[
          { label: "売上高", data: finAsc.map((f) => f.sales) },
          {
            label: "営業利益",
            data: finAsc.map((f) => f.operatingProfitLoss),
          },
          { label: "経常利益", data: finAsc.map((f) => f.ordinaryIncome) },
          {
            label: "親会社株主利益",
            data: finAsc.map((f) => f.profitLossToParent),
          },
        ]}
      />
      <DataTable
        icon={<PeopleAltOutlinedIcon />}
        title="役員"
        headers={["氏名", "役職", "生年", "保有株数", "CEO区分"]}
        rows={(listed.officers ?? []).map((o) => [
          o.name,
          o.position,
          formatBirthYear(o.dateOfBirth),
          formatNumberOrDash(o.numberOfSharesOwned, "株"),
          o.isCeo ? "CEO" : "—",
        ])}
      />
      <DataTable
        icon={<GroupsOutlinedIcon />}
        title="大株主"
        headers={["氏名・名称", "所有株式割合", "議決権割合", "所有株数"]}
        rows={(listed.majorShareholders ?? []).map((s) => [
          s.shareholderName,
          // 大株主の割合は小数の比率（0.0442 = 4.42%）。
          formatRatioAsPercent(s.ratioOfSharesHeld),
          formatRatioAsPercent(s.ratioOfVotingRightsHeld),
          formatNumberOrDash(s.numberOfSharesHeld, "株"),
        ])}
      />
      <DataTable
        icon={<FactoryOutlinedIcon />}
        title="主要な設備の状況"
        headers={[
          "事業所名",
          "所在地",
          "セグメント",
          "設備の内容",
          "帳簿価額（円）",
          "従業員数（人）",
        ]}
        rows={(listed.facilities ?? []).map((f) => [
          f.officeName,
          f.location,
          f.segment,
          f.description,
          formatYenOrDash(f.bookValue),
          formatNumberOrDash(f.employeeCount, "人"),
        ])}
      />
      <DataTable
        icon={<DescriptionOutlinedIcon />}
        title="提出書類履歴"
        headers={["書類ID", "提出日", "対象事業年度"]}
        rows={[...(listed.documents ?? [])]
          .sort((a, b) =>
            (b.submittedAt ?? "").localeCompare(a.submittedAt ?? "")
          )
          .map((d) => [d.docId, formatDate(d.submittedAt), d.fiscalYear])}
      />
    </Stack>
  );
};
